/**
 * MONSTER TOWN — OFFLINE AUDIO VERIFICATION. HUD/AUDIO/TOUCH CREW.
 *
 * "It plays a sound" is the single easiest thing to claim and never check. This
 * renders every cue in the table through an `OfflineAudioContext` — the exact
 * same `scheduleLayer` code the live path uses — and measures peak and RMS.
 *
 * A cue that renders silent, renders clipped, or throws is a FAILURE, and
 * `tools/audio-check.mjs` exits non-zero on it. Same idea as `npm run audit`
 * for placeables: a dead reaction must be impossible to ship quietly.
 */

import { CUES, resolveCue } from './cues'
import { layersDuration, makeRng, scheduleLayer } from './synth'

export interface CueMeasure {
  id: string
  ok: boolean
  peak: number
  rms: number
  seconds: number
  problem: string | null
}

export interface AudioCheckReport {
  total: number
  passed: number
  failures: CueMeasure[]
  results: CueMeasure[]
  ok: boolean
}

/** Below this peak a kid on an iPad speaker in a noisy room hears nothing. */
const MIN_PEAK = 0.01
const MIN_RMS = 0.0006
/** Above this we are asking the DAC to clip, which sounds like a fart. */
const MAX_PEAK = 0.995

const SAMPLE_RATE = 44100

function offlineCtor(): typeof OfflineAudioContext | null {
  if (typeof OfflineAudioContext !== 'undefined') return OfflineAudioContext
  const w = globalThis as { webkitOfflineAudioContext?: typeof OfflineAudioContext }
  return w.webkitOfflineAudioContext ?? null
}

/** Render one cue and measure it. Never throws — a throw becomes a failure row. */
export async function measureCue(id: string): Promise<CueMeasure> {
  const def = resolveCue(id)
  const seconds = Math.max(0.25, layersDuration(def.layers))
  const Ctor = offlineCtor()
  if (Ctor === null) {
    return { id, ok: false, peak: 0, rms: 0, seconds, problem: 'no OfflineAudioContext in this browser' }
  }
  try {
    const ctx = new Ctor(1, Math.ceil(seconds * SAMPLE_RATE), SAMPLE_RATE)
    const g = ctx.createGain()
    g.gain.value = def.gain ?? 1
    g.connect(ctx.destination)
    const rng = makeRng(0x51ed)
    for (const layer of def.layers) scheduleLayer(ctx, g, layer, 0.01, rng)
    const buf = await ctx.startRendering()
    const data = buf.getChannelData(0)
    let peak = 0
    let sum = 0
    for (let i = 0; i < data.length; i++) {
      const v = Math.abs(data[i] ?? 0)
      if (v > peak) peak = v
      sum += v * v
    }
    const rms = Math.sqrt(sum / Math.max(1, data.length))
    let problem: string | null = null
    if (!Number.isFinite(peak) || !Number.isFinite(rms)) problem = 'produced NaN samples'
    else if (peak < MIN_PEAK) problem = `silent (peak ${peak.toFixed(5)})`
    else if (rms < MIN_RMS) problem = `effectively silent (rms ${rms.toFixed(6)})`
    else if (peak > MAX_PEAK) problem = `clipping (peak ${peak.toFixed(3)})`
    return { id, ok: problem === null, peak, rms, seconds, problem }
  } catch (err) {
    return { id, ok: false, peak: 0, rms: 0, seconds, problem: `threw: ${String(err)}` }
  }
}

/** Render and measure every cue in the table. Drives tools/audio-check.mjs. */
export async function checkAllCues(): Promise<AudioCheckReport> {
  const results: CueMeasure[] = []
  for (const def of CUES) results.push(await measureCue(def.id))
  const failures = results.filter((r) => !r.ok)
  return {
    total: results.length,
    passed: results.length - failures.length,
    failures,
    results,
    ok: failures.length === 0,
  }
}
